import React from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { Row, Col, Flex, Card, Divider, Typography, Tooltip } from "antd"
import { isUserSelector } from "store/user"
import SummaryKeyValue from "ui/Booking/Details/Body/PaymentSummary/SummaryKeyValue"
import useSummaryDetails from "ui/Booking/Details/Body/PaymentSummary/hooks/useSummaryDetails"
import { numberToCurrency } from "helpers/string"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import { displayPaymentStatus } from "ui/Booking/Details/utils"
import moment from "moment"
import styles from "../../index.module.css"
import { Icon } from "ui/Icon"
import AddOnPhoto from "ui/AddOnPhoto"
import { useTranslation } from "react-i18next"

const { Text } = Typography

const getPaymentHistoryKey = (payment = {}) => {
  const { type, paid_on, method, last4, amount } = payment
  return [type, paid_on, method, last4, amount].filter((value) => value !== undefined && value !== null && value !== "").join("-")
}

const PaymentCard = React.memo(({ paymentStatus, paymentSummary, paymentHistory, pricing, bookingAddOns }) => {
  const { t } = useTranslation()
  const isGuest = useSelector(isUserSelector)
  const { total_price, remaining_balance, adjust_price_history, subtotal_adjusted } = paymentSummary || {}
  // For guests, a negotiated subtotal is shown as a plain figure with no derivation — the
  // adjusted-price breakdown, add-ons, and discount lines are guide-only.
  const hideAdjustedBreakdown = subtotal_adjusted && isGuest
  const allDetails = useSummaryDetails(paymentSummary, pricing, { combineFees: true, showSubtotalAdjustment: !isGuest })
  const hasPayments = paymentHistory?.length > 0
  const hasAddOns = bookingAddOns?.length > 0 && !hideAdjustedBreakdown

  const priceLines = allDetails.filter((d) => !d.combinedFee && !d.isCardFee)
  const feeRow = allDetails.find((d) => d.combinedFee)
  const discountIndex = priceLines.findIndex((d) => d?.isDiscount)
  const discountLines = discountIndex >= 0 && !hideAdjustedBreakdown ? [priceLines[discountIndex]] : []
  const preDiscountLines = discountIndex >= 0 ? priceLines.slice(0, discountIndex) : priceLines
  const postDiscountLines = discountIndex >= 0 ? priceLines.slice(discountIndex + 1) : []
  const cardFeeRow = allDetails.find((d) => d.isCardFee)

  const subtotalIndex = preDiscountLines.findIndex((d) => d.isSubtotal)
  const pricingLines = subtotalIndex >= 0 ? preDiscountLines.slice(0, subtotalIndex) : preDiscountLines
  const subtotalLine = subtotalIndex >= 0 ? [preDiscountLines[subtotalIndex]] : []
  const postSubtotalLines = subtotalIndex >= 0 ? preDiscountLines.slice(subtotalIndex + 1) : []

  return (
    <Card className={styles.mainCard} title="Payment">
      <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
        <Col xl={hasPayments ? 12 : 24} span={24}>
          <div className={styles.priceDetails}>
            <Flex align="center" gap={16} justify="space-between">
              <Text className="color-text-secondary-dark" strong>
                Price Details
              </Text>
              <StatusTag status={displayPaymentStatus(paymentStatus)} />
            </Flex>
            <Flex vertical gap={8} className="mt-l">
              <SummaryKeyValue details={pricingLines} valueAlign="right" />
              {hasAddOns && (
                <div>
                  <Divider className="mt-0 mb-xs" />
                  <Text strong>{t("booking.add_ons.title", { defaultValue: "Add-ons" })}</Text>
                  {bookingAddOns.map((a) => (
                    <Row key={a.id} align="middle" gutter={8} className="mt-xs">
                      <Col xs={14} md={12}>
                        <Flex align="center" gap={8} className={styles.addOnTitleCell}>
                          <AddOnPhoto src={a.photo_url} alt={a.title} size={32} iconSize={16} />
                          <Flex vertical className={styles.addOnTitleCell}>
                            <Tooltip title={a.title} placement="topLeft">
                              <Text strong className={styles.addOnTitle}>{a.title}</Text>
                            </Tooltip>
                            <Text type="secondary" className={styles.addOnSubtitle}>{`${a.quantity} × ${numberToCurrency(Number(a.unit_price))}`}</Text>
                          </Flex>
                        </Flex>
                      </Col>
                      <Col xs={10} md={12} className={styles.alignRight}>
                        <Text strong>{numberToCurrency(Number(a.total_price))}</Text>
                      </Col>
                    </Row>
                  ))}
                  <Divider className="mt-xs mb-0" />
                </div>
              )}
              {subtotalLine.length > 0 && <SummaryKeyValue details={subtotalLine} valueAlign="right" />}
              {postSubtotalLines.length > 0 && <SummaryKeyValue details={postSubtotalLines} valueAlign="right" />}
              {discountLines.length > 0 && <SummaryKeyValue details={discountLines} valueAlign="right" />}
              {postDiscountLines.length > 0 && <SummaryKeyValue details={postDiscountLines} valueAlign="right" />}
              {feeRow && (
                <>
                  <Divider style={{ margin: 0 }} />
                  <Row gutter={[0, 0]}>
                    <Col xs={14} md={12}>
                      <Text>Tax &amp; Fees</Text>
                    </Col>
                    <Col xs={10} md={12} style={{ textAlign: "right" }}>
                      <Text>{numberToCurrency(feeRow.value)}</Text>
                    </Col>
                  </Row>
                </>
              )}
              {cardFeeRow && (
                <Row gutter={[0, 0]}>
                  <Col xs={14} md={12}>
                    <Text>Card Processing Fee</Text>
                  </Col>
                  <Col xs={10} md={12} style={{ textAlign: "right" }}>
                    <Text>{numberToCurrency(cardFeeRow.value)}</Text>
                  </Col>
                </Row>
              )}
              {adjust_price_history?.map((item) => {
                const { price_difference, reason, id } = item || {}
                return (
                  <Flex key={id || `${price_difference}-${reason}`} align="center" justify="space-between">
                    <Flex align="center" gap={4}>
                      <Text>Price Change</Text>
                      {reason && (
                        <Tooltip title={reason}>
                          <span style={{ display: "inline-flex" }}>
                            <Icon type="feather" name="Info" width={16} height={16} />
                          </span>
                        </Tooltip>
                      )}
                    </Flex>
                    <Text>
                      {price_difference > 0 ? "+" : ""}
                      {numberToCurrency(price_difference)}
                    </Text>
                  </Flex>
                )
              })}
              <Divider style={{ margin: 0 }} />
              <Flex align="center" justify="space-between">
                <Text strong={!hasPayments}>Total</Text>
                <Text strong={!hasPayments}>{numberToCurrency(total_price)}</Text>
              </Flex>
              {hasPayments && remaining_balance !== undefined && (
                <Flex align="center" justify="space-between">
                  <Text strong>Remaining Balance</Text>
                  <Text strong>{numberToCurrency(remaining_balance)}</Text>
                </Flex>
              )}
            </Flex>
          </div>
        </Col>
        {hasPayments && (
          <Col xl={12} span={24} style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.priceDetails}>
              <Text className="color-text-secondary-dark" strong>
                Payment History
              </Text>
              <div className={`${styles.paymentMethodScroll} mt-s px-s`}>
                {paymentHistory.map((payment, index) => {
                  const { type, paid_on, method, last4, amount, id, payer_name } = payment || {}
                  const isRefund = type?.toLowerCase() === "refund"
                  return (
                    <React.Fragment key={getPaymentHistoryKey(payment)}>
                      <Row gutter={[10, 10]} className={index > 0 ? "mt-s" : ""}>
                        <Col sm={14} span={24}>
                          <Flex vertical>
                            <Text>
                              {type || ""} ({method || ""}
                              {last4 && ` **** ${last4}`})
                            </Text>
                            <Text style={{ color: isRefund ? "var(--color-error)" : "inherit" }}>
                              {amount === undefined ? "" : numberToCurrency(amount)}
                            </Text>
                            {payer_name && <Text className="color-text-secondary-dark">Paid by {payer_name}</Text>}
                          </Flex>
                        </Col>
                        <Col sm={10} span={24}>
                          {paid_on && (
                            <Flex vertical>
                              <Text className="color-text-secondary-dark">{isRefund ? "Refunded on" : "Paid on"}</Text>
                              <Text>{moment(paid_on).format("MM/DD/YYYY h:mm A")}</Text>
                            </Flex>
                          )}
                        </Col>
                      </Row>
                      {index < paymentHistory.length - 1 && <Divider className="my-xs" />}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  )
})

PaymentCard.propTypes = {
  paymentStatus: PropTypes.string,
  paymentSummary: PropTypes.shape({
    total_price: PropTypes.number,
    remaining_balance: PropTypes.number,
    adjust_price_history: PropTypes.array
  }),
  paymentHistory: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      last4: PropTypes.string,
      type: PropTypes.string,
      method: PropTypes.string,
      paid_on: PropTypes.string
    })
  ),
  pricing: PropTypes.object,
  bookingAddOns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      quantity: PropTypes.number,
      unit_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      photo_url: PropTypes.string
    })
  )
}

PaymentCard.displayName = "PaymentCard"

export default PaymentCard
